import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const {
  FiX, FiShare2, FiCopy, FiCheck,
  FiMail, FiLink, FiUsers, FiSend,
} = FiIcons;

const AVATAR = (name = '?') =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1E3A5F&color=C9A84C&bold=true&size=64`;

const BASE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://memorialsanctuary.com';

const InviteModal = ({ node, onClose }) => {
  const inviteLink = `${BASE_URL}/invite?ref=${encodeURIComponent(node?.name ?? '')}&rel=${encodeURIComponent(node?.relation ?? '')}`;
  const [copied,   setCopied]   = useState(false);
  const [email,    setEmail]    = useState('');
  const [sent,     setSent]     = useState(false);
  const [sending,  setSending]  = useState(false);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); }
    catch { /* fallback: select */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const sendEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setTimeout(() => { setSending(false); setSent(true); }, 1400);
  };

  const shareNative = () => {
    if (navigator.share) {
      navigator.share({
        title: `Join ${node?.name} on Memorial Sanctuary`,
        text:  `You've been invited to connect with ${node?.name} (${node?.relation}) on Memorial Sanctuary.`,
        url:   inviteLink,
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} />

        <motion.div
          className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.92, y: 24 }}
          animate={{ opacity: 1, scale: 1,    y: 0  }}
          exit={{   opacity: 0, scale: 0.92, y: 24  }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
        >
          {/* Header */}
          <div className="relative bg-accent-navy px-6 py-6 text-white">
            <button onClick={onClose}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-gold/20 border border-accent-gold/40 rounded-2xl flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-accent-gold" />
              </div>
              <div>
                <h3 className="font-serif text-lg">Invite to Platform</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-white/50 mt-0.5">
                  Connect with {node?.name}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Member preview */}
            <div className="flex items-center gap-4 p-4 bg-brand-light rounded-2xl border border-brand-dark">
              <img src={AVATAR(node?.name)} alt={node?.name}
                className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm" />
              <div>
                <p className="font-bold text-accent-navy text-sm">{node?.name}</p>
                <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold mt-0.5">{node?.relation}</p>
              </div>
            </div>

            {/* Invite link */}
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2 flex items-center gap-2">
                <SafeIcon icon={FiLink} className="w-3 h-3" /> Shareable Link
              </p>
              <div className="flex gap-2">
                <div className="flex-grow bg-brand-light border-2 border-brand-dark rounded-xl px-4 py-2.5 text-xs font-mono text-text-muted truncate">
                  {inviteLink}
                </div>
                <button onClick={copyLink}
                  className={`px-4 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all flex items-center gap-1.5 shrink-0 ${
                    copied
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-white border-brand-dark text-accent-navy hover:border-accent-gold'
                  }`}>
                  <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Email invite */}
            {!sent ? (
              <form onSubmit={sendEmail}>
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiMail} className="w-3 h-3" /> Send Email Invite
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={`${node?.name?.split(' ')[0]}'s email address…`}
                    className="flex-grow bg-white border-2 border-brand-dark rounded-xl px-4 py-2.5 text-sm outline-none focus:border-accent-gold transition-colors"
                  />
                  <button type="submit" disabled={!email.trim() || sending}
                    className="px-4 py-2.5 bg-accent-navy text-white rounded-xl text-[9px] font-black uppercase tracking-widest border-2 border-accent-navy hover:bg-accent-navy/90 transition-all disabled:opacity-40 flex items-center gap-1.5 shrink-0">
                    {sending ? (
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <SafeIcon icon={FiSend} className="w-3.5 h-3.5" />
                    )}
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            ) : (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl text-green-700">
                <SafeIcon icon={FiCheck} className="w-5 h-5 shrink-0" />
                <div>
                  <p className="font-bold text-sm">Invite sent!</p>
                  <p className="text-xs text-green-600 mt-0.5">
                    {node?.name} will receive an email with a link to join Memorial Sanctuary.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Native share */}
            <button onClick={shareNative}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-brand-dark text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
              Share via other apps
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InviteModal;